import React, { useCallback, useEffect, useRef, useState } from 'react';
import { QuestionCircleOutlined } from '@ant-design/icons';
import {
  Button,
  ConfigProvider,
  Input,
  Modal,
  message,
  Segmented,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import { useAuth } from '../../hooks/useAuth';
import { getProjectList } from '../../services/project';
import {
  getReturnInspectionRule,
  listProjectUnits,
  listReturnInspectionRules,
  removeReturnInspectionRule,
} from '../../services/returnInspection';
import {
  ProjectUnitRow,
  ProjectUnitsResponse,
  ReturnInspectionRule,
  RULE_SOURCE_LABEL,
  ScopeLevel,
  SourceFilter,
  UnitContentItem,
} from '../../types/returnInspection';
import RuleLevelCard, { ConnectorRow, LEVEL_DESCRIPTION } from './RuleLevelCard';
import RuleFormDrawer from './RuleFormDrawer';
import BulkApplyRuleModal, { BulkTargets } from './BulkApplyRuleModal';
import RuleHelpModal from './RuleHelpModal';
import RuleSummary from './RuleSummary';
import UnitRulePreview from './UnitRulePreview';
import { LEVEL_COLOR, LEVEL_NAME, LevelBadge, ModeBadge, WARNING_COLOR } from './ruleDisplay';

const { Text } = Typography;

const UNIT_PAGE_SIZE = 10;
/** 批量设置规则一次最多处理的单元数（和后端一致） */
const BULK_MAX_TARGETS = 200;

interface ProjectOption {
  id: number;
  label: string;
}

/**
 * 内容项展示格式：产品名称(编码:客户自定义编码 条码:第一个分拣条码 等N个)
 * 例：黑色新款手机(编码:ABC123-NEW 条码:X00F12AF3DE 等2个)
 * - 分拣条码只有 1 个时不显示"等N个"；N 是该产品分拣条码的总数
 * - 缺编码或缺条码时省略对应部分，括号里什么都没有时连括号一起省略
 */
const formatContentItem = (item: UnitContentItem): string => {
  const name = item.product_name || item.product_code || '-';
  const codes = (item.sorting_codes || []).map((c) => c.code).filter((c): c is string => !!c);
  const parts: string[] = [];
  if (item.product_code) parts.push(`编码:${item.product_code}`);
  if (codes.length) {
    parts.push(`条码:${codes[0]}${codes.length > 1 ? ` 等${codes.length}个` : ''}`);
  }
  return parts.length ? `${name}(${parts.join(' ')})` : name;
};

interface DrawerState {
  scopeLevel: ScopeLevel;
  scopeId?: number;
  /** 管理员设置通用规则时的目标客户 */
  customerId?: number;
  scopeLabel: string;
  existingRule: ReturnInspectionRule | null;
}

interface ReturnInspectionRulesPageProps {
  /**
   * 管理员页面传入：已选定的目标客户。用来过滤项目下拉，并作为通用规则的归属客户。
   * 客户账号不需要传——项目范围和通用规则的归属都由后端按登录身份限定。
   */
  adminCustomerId?: number;
  /** 管理员包装页放在最上方的额外筛选控件（客户选择下拉） */
  extraFiltersStart?: React.ReactNode;
}

const readProjectIdFromUrl = (): number | undefined => {
  try {
    const value = new URL(window.location.href).searchParams.get('project_id');
    return value && /^\d+$/.test(value) ? Number(value) : undefined;
  } catch {
    return undefined;
  }
};

const writeProjectIdToUrl = (projectId?: number) => {
  try {
    const url = new URL(window.location.href);
    if (projectId) url.searchParams.set('project_id', String(projectId));
    else url.searchParams.delete('project_id');
    window.history.replaceState({}, '', url.toString());
  } catch {
    // URL 同步只是方便刷新后保持选择，失败不影响功能
  }
};

const extractErrorMessage = (e: any, fallback: string): string => {
  const data = e?.response?.data;
  if (typeof data?.detail === 'string') return data.detail;
  const first = data && typeof data === 'object' ? Object.values(data)[0] : null;
  const text = Array.isArray(first) ? first[0] : first;
  return typeof text === 'string' ? text : fallback;
};

const ReturnInspectionRulesPage: React.FC<ReturnInspectionRulesPageProps> = ({
  adminCustomerId,
  extraFiltersStart,
}) => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const isSubAccount = user?.role === 'customer' && user?.customer_sub_role === 'sub_account';

  const [projects, setProjects] = useState<ProjectOption[]>([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  // 管理员必须先选客户，再选该客户的项目（客户 → 项目 → 库存单元），所以管理员还没选客户时，
  // URL 里残留的 project_id 一律不采用
  const [projectId, setProjectId] = useState<number | undefined>(() =>
    isAdmin && !adminCustomerId ? undefined : readProjectIdFromUrl(),
  );

  // 页面最上方"通用规则"卡片的数据（按客户查，不依赖项目）
  const [customerRule, setCustomerRule] = useState<ReturnInspectionRule | null>(null);
  const [customerRuleLoading, setCustomerRuleLoading] = useState(false);

  const [projectData, setProjectData] = useState<ProjectUnitsResponse | null>(null);
  const [dataLoading, setDataLoading] = useState(false);
  const [page, setPage] = useState(1);
  // search 是真正用来查询的关键字；searchInput 是搜索框里当前输入的内容。两者分开，是为了边输入边筛选
  // 时不必每敲一个字就发一次请求：停止输入一小会儿后才把 searchInput 同步成 search（见下面的 useEffect）
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [sourceFilter, setSourceFilter] = useState<SourceFilter | undefined>(undefined);
  // 库存单元列表默认不展开也不加载：点库存单元级卡片上的"设置"才加载并显示（再点"收起"隐藏）
  const [unitListOpen, setUnitListOpen] = useState(false);
  // 库存单元级规则卡片默认不显示：点过渡行上的"展开库存单元级规则设置"才出现；
  // 卡片里的"设置/收起"再控制下面的库存单元列表（两层展开互相独立）
  const [unitCardOpen, setUnitCardOpen] = useState(false);

  const [drawer, setDrawer] = useState<DrawerState | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  // 批量设置规则：列表勾选的单元（翻页保留），或者"当前筛选结果的全部"
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [selectAllFiltered, setSelectAllFiltered] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  useEffect(() => {
    setSelectedIds([]);
    setSelectAllFiltered(false);
  }, [projectId, sourceFilter, search]);

  // 项目/分页/筛选切换时可能有多个请求在途，只采纳最后一次发起的那个，避免旧结果覆盖新结果
  const loadSeq = useRef(0);

  const units = projectData?.results ?? [];
  const summary = projectData?.summary;

  // ---------- 项目下拉 ----------
  useEffect(() => {
    const load = async () => {
      // 管理员还没选客户：不加载项目，项目下拉保持禁用（先选客户，再选该客户的项目）
      if (isAdmin && !adminCustomerId) {
        setProjects([]);
        return;
      }
      try {
        setProjectsLoading(true);
        const res = await getProjectList({ page_size: 1000, customer: adminCustomerId });
        const data: any = res.data;
        const rows: any[] = Array.isArray(data) ? data : data?.results || [];
        setProjects(
          rows.map((p) => ({
            id: p.id,
            label: `${p.project_code} - ${p.name || '未命名项目'}`,
          })),
        );
      } catch {
        message.error('加载项目列表失败');
        setProjects([]);
      } finally {
        setProjectsLoading(false);
      }
    };
    load();
  }, [adminCustomerId, isAdmin]);

  const handleProjectChange = (value?: number) => {
    setProjectId(value);
    setPage(1);
    setSearch('');
    setSearchInput('');
    setSourceFilter(undefined);
    setUnitListOpen(false);
    setUnitCardOpen(false);
    setProjectData(null);
    writeProjectIdToUrl(value);
  };

  // 搜索框实时筛选：停止输入 300ms 后自动按当前关键字查询（回车或点搜索图标则立即查询），
  // 不需要手动触发。连续输入时只会发出最后一次请求，关键字变了就回到第一页。
  useEffect(() => {
    const keyword = searchInput.trim();
    if (keyword === search) return;
    const timer = window.setTimeout(() => {
      setSearch(keyword);
      setPage(1);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [searchInput, search]);

  // 管理员还没选客户时，项目选择不可用：URL 里残留的 project_id 要清掉（初始状态已经忽略了它，
  // 这里连 URL 一起清；用户信息比页面晚一拍才到时，也靠这里把已经选上的项目撤掉）
  useEffect(() => {
    if (!isAdmin || adminCustomerId) return;
    if (projectId) handleProjectChange(undefined);
    else writeProjectIdToUrl(undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, adminCustomerId]);

  // ---------- 通用规则（页面最上方的独立区块） ----------
  // 通用规则属于客户，不依赖选了哪个项目。主账号/子账号按登录身份限定到自己的客户（子账号只读），
  // 管理员需要先选定客户。
  const canLoadCustomerRule = !isAdmin || !!adminCustomerId;

  const loadCustomerRule = useCallback(async () => {
    if (!canLoadCustomerRule) {
      setCustomerRule(null);
      return;
    }
    try {
      setCustomerRuleLoading(true);
      const rows = await listReturnInspectionRules({
        scope_level: 'customer',
        state: 'active',
        customer_id: isAdmin ? adminCustomerId : undefined,
      });
      setCustomerRule(rows[0] || null);
    } catch {
      message.error('加载通用规则失败');
      setCustomerRule(null);
    } finally {
      setCustomerRuleLoading(false);
    }
  }, [canLoadCustomerRule, isAdmin, adminCustomerId]);

  useEffect(() => {
    loadCustomerRule();
  }, [loadCustomerRule]);

  // ---------- 选定项目后：一次请求拿到 库存单元 + 规则来源 + 覆盖概览 + 通用/项目级规则 ----------
  const loadProjectData = useCallback(async () => {
    if (!projectId) return;
    const seq = ++loadSeq.current;
    try {
      setDataLoading(true);
      // 列表没展开时只需要卡片上的数据（通用/项目级规则 + 各来源的单元数量），这些都在响应的
      // 顶层字段里，与分页的行无关，所以只取 1 行，避免白白加载一整页库存单元；展开后才取真正的一页。
      const data = await listProjectUnits({
        project_id: projectId,
        page: unitListOpen ? page : 1,
        page_size: unitListOpen ? UNIT_PAGE_SIZE : 1,
        search: unitListOpen ? search || undefined : undefined,
        source: unitListOpen ? sourceFilter : undefined,
      });
      if (seq !== loadSeq.current) return;
      setProjectData(data);
      // 正在看的筛选项，其数量因为刚做的设置/移除变成了 0：退回"全部"，不要停在一个空列表上
      if (unitListOpen && sourceFilter && data.summary[sourceFilter] === 0)
        setSourceFilter(undefined);
    } catch (e: any) {
      if (seq !== loadSeq.current) return;
      // 当前页因为数据减少已经不存在（例如最后一页的最后一个单元刚变了来源）：回到第一页
      if (unitListOpen && e?.response?.status === 404 && page > 1) {
        setPage(1);
        return;
      }
      message.error(extractErrorMessage(e, '加载项目规则失败'));
      setProjectData(null);
    } finally {
      if (seq === loadSeq.current) setDataLoading(false);
    }
  }, [projectId, page, search, sourceFilter, unitListOpen]);

  useEffect(() => {
    loadProjectData();
  }, [loadProjectData]);

  const reloadAll = () => {
    loadCustomerRule();
    loadProjectData();
  };

  // ---------- 页面最上方的"通用规则"区块 + 选定项目后的项目级规则 ----------
  const projectLabel = projects.find((p) => p.id === projectId)?.label || '';
  // 通用规则的编辑权限：子账号只读；管理员需要先选定客户（通用规则属于客户）
  const customerRuleReady = !isAdmin || !!adminCustomerId;
  const canEditCustomerRule = !isSubAccount && customerRuleReady;

  // ---------- 打开编辑抽屉 ----------
  const openCustomerDrawer = () =>
    setDrawer({
      scopeLevel: 'customer',
      customerId: isAdmin ? adminCustomerId : undefined,
      scopeLabel: isAdmin ? '适用于该客户的所有项目' : '适用于我的所有项目',
      existingRule: customerRule,
    });

  const openProjectDrawer = () =>
    setDrawer({
      scopeLevel: 'project',
      scopeId: projectId,
      scopeLabel: projectLabel,
      existingRule: projectData?.project_rule || null,
    });

  const openUnitDrawer = async (unit: ProjectUnitRow) => {
    let existing: ReturnInspectionRule | null = null;
    if (unit.source === 'unit' && unit.rule_id) {
      try {
        existing = await getReturnInspectionRule(unit.rule_id);
      } catch {
        message.error('加载规则详情失败');
        return;
      }
    }
    setDrawer({
      scopeLevel: 'inventory_unit',
      scopeId: unit.inventory_unit_id,
      scopeLabel: unit.system_barcode,
      existingRule: existing,
    });
  };

  // ---------- 移除本级规则（移除后自动回退到上级规则） ----------
  // 确认弹窗只有标题（右侧带上被移除规则所属的项目/库存单元）和按钮；通用规则因为移除后果比较重，多一段说明。
  const confirmRemove = (opts: {
    title: string;
    /** 紧跟在标题右侧的附加信息，如当前项目 */
    titleExtra?: string;
    /** 弹窗正文；不传就不显示正文 */
    content?: string;
    ruleId: number;
    /** 移除后是否还有上级规则可用，只影响成功提示的措辞 */
    hasUpperRule: boolean;
  }) => {
    Modal.confirm({
      title: opts.titleExtra ? (
        <span>
          {opts.title}
          <span style={{ marginLeft: 12, fontSize: 13, fontWeight: 400, color: '#595959' }}>
            {opts.titleExtra}
          </span>
        </span>
      ) : (
        opts.title
      ),
      okText: '确认移除',
      okButtonProps: { danger: true },
      cancelText: '取消',
      content: opts.content ? <div>{opts.content}</div> : undefined,
      onOk: async () => {
        try {
          await removeReturnInspectionRule(opts.ruleId);
          message.success(opts.hasUpperRule ? '已移除，改用上级规则' : '已移除');
          reloadAll();
        } catch (e: any) {
          message.error(extractErrorMessage(e, '移除失败'));
          return Promise.reject(e); // 失败时保持弹窗打开
        }
      },
    });
  };

  const removeUnitRule = (unit: ProjectUnitRow) => {
    if (!unit.rule_id) return;
    confirmRemove({
      title: '移除库存单元级规则？',
      titleExtra: `库存单元: ${unit.system_barcode}`,
      ruleId: unit.rule_id,
      hasUpperRule: !!(projectData?.project_rule || projectData?.customer_rule),
    });
  };

  const removeProjectRule = () => {
    const rule = projectData?.project_rule;
    if (!rule) return;
    confirmRemove({
      title: '移除项目级规则？',
      titleExtra: projectLabel ? `当前项目: ${projectLabel}` : undefined,
      ruleId: rule.id,
      hasUpperRule: !!projectData?.customer_rule,
    });
  };

  const removeCustomerRule = () => {
    if (!customerRule) return;
    confirmRemove({
      title: '移除通用规则？',
      content:
        '通用规则作为兜底规则适用于所有项目的所有库存单元。若移除通用规则，则必须为需要创建退货入库预报的特定项目设置项目级规则或为特定库存单元设置库存单元级规则，否则将无法创建退货入库预报。',
      ruleId: customerRule.id,
      hasUpperRule: false,
    });
  };

  // 批量设置规则的选择情况
  const filteredCount = projectData?.count ?? 0;
  const selectedCount = selectAllFiltered ? filteredCount : selectedIds.length;
  const bulkTargets: BulkTargets = selectAllFiltered
    ? { kind: 'filtered', count: filteredCount, source: sourceFilter, search: search || undefined }
    : { kind: 'ids', ids: selectedIds };

  // 库存单元列表（覆盖概览 + 搜索 + 表格）。它不再是一张独立的卡片，而是放进"库存单元级规则"卡片的
  // 展开内容里，整个板块只有一圈绿色边框；点卡片里的"设置"才会显示（并且才开始加载这一页数据）。
  const unitListContent = (
    <>
      <div style={{ marginBottom: 12 }}>
        本项目{projectLabel ? `（${projectLabel}）` : ''}内所有库存单元最终生效规则如下：
      </div>
      {summary && (
        // 覆盖概览：整个项目各来源的单元数量，点击即在服务端筛选，快速找出"还没配规则"的单元
        <div style={{ marginBottom: 12, overflowX: 'auto' }}>
          <Segmented
            value={sourceFilter ?? 'all'}
            onChange={(v) => {
              setSourceFilter(v === 'all' ? undefined : (v as SourceFilter));
              setPage(1);
            }}
            options={[
              { value: 'all', label: `全部 ${summary.total}` },
              {
                value: 'unit',
                label: `库存单元自设 ${summary.unit}`,
                disabled: summary.unit === 0,
              },
              {
                value: 'project',
                label: `使用项目级规则 ${summary.project}`,
                disabled: summary.project === 0,
              },
              {
                value: 'customer',
                label: `使用通用规则 ${summary.customer}`,
                disabled: summary.customer === 0,
              },
              {
                value: 'none',
                label: (
                  <span style={{ color: summary.none ? WARNING_COLOR : undefined }}>
                    无规则 {summary.none}
                  </span>
                ),
                disabled: summary.none === 0,
              },
            ]}
          />
        </div>
      )}
      <Space style={{ marginBottom: 12, width: '100%', justifyContent: 'space-between' }} wrap>
        <Space wrap>
          <Input.Search
            allowClear
            placeholder="搜索库存单元条码 / 分拣组 / 产品编码 / 产品名称 / 分拣条码"
            style={{ width: 420, maxWidth: '100%' }}
            value={searchInput}
            // 边输入边筛选：输入框里的内容立即更新，查询由上面的防抖 useEffect 触发
            onChange={(e) => setSearchInput(e.target.value)}
            // 回车或点搜索图标：不等防抖，立即查询
            onSearch={(v) => {
              setSearchInput(v);
              setSearch(v.trim());
              setPage(1);
            }}
          />
          <Button onClick={reloadAll}>刷新</Button>
        </Space>
      </Space>
      {/* 批量设置规则：勾选单元后，把某个单元的规则策略一次设置给它们 */}
      <div
        style={{
          marginBottom: 12,
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <Button disabled={selectedCount === 0} onClick={() => setBulkOpen(true)}>
          批量设置规则
        </Button>
        <span style={{ color: '#595959' }}>
          {selectAllFiltered
            ? `已选当前筛选结果的全部 ${filteredCount} 个单元`
            : selectedCount > 0
              ? `已选 ${selectedCount} 个单元`
              : '勾选单元后可批量设置规则'}
        </span>
        {!selectAllFiltered &&
          selectedCount > 0 &&
          filteredCount > selectedCount &&
          (filteredCount <= BULK_MAX_TARGETS ? (
            <Button type="link" style={{ padding: 0 }} onClick={() => setSelectAllFiltered(true)}>
              选择当前筛选结果的全部 {filteredCount} 个单元
            </Button>
          ) : (
            <span style={{ color: '#999', fontSize: 12 }}>
              筛选结果超过 {BULK_MAX_TARGETS} 个，一次最多处理 {BULK_MAX_TARGETS} 个，请缩小筛选范围
            </span>
          ))}
        {selectedCount > 0 && (
          <Button
            type="link"
            style={{ padding: 0 }}
            onClick={() => {
              setSelectedIds([]);
              setSelectAllFiltered(false);
            }}
          >
            清除选择
          </Button>
        )}
      </div>
      <Table<ProjectUnitRow>
        rowKey="inventory_unit_id"
        rowSelection={{
          selectedRowKeys: selectAllFiltered ? units.map((u) => u.inventory_unit_id) : selectedIds,
          preserveSelectedRowKeys: true,
          onChange: (keys) => {
            setSelectAllFiltered(false);
            setSelectedIds(keys as number[]);
          },
        }}
        size="middle"
        loading={dataLoading}
        dataSource={units}
        pagination={{
          current: page,
          pageSize: UNIT_PAGE_SIZE,
          total: projectData?.count ?? 0,
          showSizeChanger: false,
          showTotal: (t) => `共 ${t} 个库存单元`,
          onChange: setPage,
        }}
        // 卡片内部比整页窄一点：内容项这一列放不下时出现横向滚动，而不是把文字压得换行
        scroll={{ x: 'max-content' }}
        locale={{ emptyText: '没有符合条件的散货库存单元' }}
        columns={[
          {
            title: '库存单元系统编码',
            dataIndex: 'system_barcode',
            render: (value: string) => <span style={{ fontWeight: 500 }}>{value}</span>,
          },
          {
            title: '库存单元内容项',
            render: (_, row) => {
              if (!row.items.length) return <Text type="secondary">-</Text>;
              return (
                <div>
                  {row.items.map((item, idx) => (
                    <div key={`${item.product_code}-${idx}`}>
                      <span>{formatContentItem(item)}</span>
                      {(item.quantity || 1) > 1 && <Text type="secondary"> ×{item.quantity}</Text>}
                    </div>
                  ))}
                </div>
              );
            },
          },
          {
            title: '最终生效规则',
            render: (_, row) => {
              // 三级都没有规则：红色醒目提示，并说明后果
              if (!row.source) {
                return (
                  <div>
                    <Tag color="error">无规则</Tag>
                    <div style={{ fontSize: 12, color: WARNING_COLOR }}>无法创建退货入库预报</div>
                  </div>
                );
              }
              const own = row.source === 'unit';
              // 结果在上，来源在下；文字都用默认文字色，来源徽标按层级各用自己的颜色（统一为描边、无底色）
              const cellContent = (
                <div>
                  <ModeBadge mode={row.mode} />
                  <div style={{ marginTop: 2, fontSize: 12 }}>
                    来自：
                    <LevelBadge
                      level={
                        own ? 'inventory_unit' : row.source === 'project' ? 'project' : 'customer'
                      }
                      text={own ? '库存单元自设' : RULE_SOURCE_LABEL[row.source]}
                      outlined
                    />
                  </div>
                  {row.content_baseline_missing && (
                    <div style={{ marginTop: 2, fontSize: 12, color: '#d46b08' }}>
                      未提供内件基准资料
                    </div>
                  )}
                </div>
              );
              // 库存单元自设的规则：鼠标悬停时显示这条规则的简要内容
              return own && row.rule_id ? (
                <UnitRulePreview ruleId={row.rule_id}>{cellContent}</UnitRulePreview>
              ) : (
                cellContent
              );
            },
          },
          {
            title: '操作',
            width: 190,
            render: (_, row) =>
              row.source === 'unit' ? (
                <Space size={0}>
                  <Button type="link" onClick={() => openUnitDrawer(row)}>
                    编辑规则
                  </Button>
                  <Button type="link" danger onClick={() => removeUnitRule(row)}>
                    移除规则
                  </Button>
                </Space>
              ) : (
                <Button type="link" onClick={() => openUnitDrawer(row)}>
                  设置规则
                </Button>
              ),
          },
        ]}
      />
    </>
  );

  // ---------- 渲染 ----------
  return (
    <div style={{ padding: 24 }}>
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        {/* 页面级说明：适用场景、三级优先级、仓库入库时的执行步骤 */}
        <div>
          <Button
            type="link"
            icon={<QuestionCircleOutlined />}
            style={{ padding: 0 }}
            onClick={() => setHelpOpen(true)}
          >
            退货入库初检说明
          </Button>
        </div>
        {extraFiltersStart && <div>{extraFiltersStart}</div>}

        {/* 三个层级自上而下排列，由宽到窄：通用规则 → 项目级规则 → 库存单元级规则。
            默认只显示通用规则；每一级之间的"过渡行"（说明文字 + 控件）负责展开下一级。
            三张卡片用同一个组件，大小、宽度、版式完全一致，只有颜色不同。 */}
        <RuleLevelCard
          level="customer"
          title={LEVEL_NAME.customer}
          description={LEVEL_DESCRIPTION.customer}
          placeholder={
            customerRuleReady ? undefined : '请先在上方选择客户，才能设置该客户的通用规则。'
          }
          onEdit={canEditCustomerRule ? openCustomerDrawer : undefined}
          onRemove={canEditCustomerRule && customerRule ? removeCustomerRule : undefined}
          hasRule={!!customerRule}
          loading={customerRuleLoading}
        >
          {customerRule ? <RuleSummary rule={customerRule} /> : <Text type="secondary">无</Text>}
          {isSubAccount && customerRule && (
            <div style={{ marginTop: 4, fontSize: 12, color: '#999' }}>只读：由主账号维护</div>
          )}
        </RuleLevelCard>

        <ConnectorRow text="对不适用于通用规则的特定项目，可查看或设置项目级规则，请先选择项目">
          <Select
            showSearch
            allowClear
            style={{ width: 320, maxWidth: '100%' }}
            placeholder={
              isAdmin && !adminCustomerId
                ? '请先在上方选择客户'
                : isSubAccount
                  ? '选择分配给你的项目'
                  : '选择项目'
            }
            disabled={isAdmin && !adminCustomerId}
            loading={projectsLoading}
            value={projectId}
            optionFilterProp="label"
            options={projects.map((p) => ({ value: p.id, label: p.label }))}
            onChange={(v) => handleProjectChange(v as number | undefined)}
            notFoundContent={projectsLoading ? '加载中...' : '没有可管理的项目'}
          />
        </ConnectorRow>

        {projectId && (
          <>
            <RuleLevelCard
              level="project"
              title={LEVEL_NAME.project}
              titleExtra={projectLabel ? `当前项目: ${projectLabel}` : undefined}
              description={LEVEL_DESCRIPTION.project}
              onEdit={projectData ? openProjectDrawer : undefined}
              onRemove={projectData?.project_rule ? removeProjectRule : undefined}
              hasRule={!!projectData?.project_rule}
              loading={dataLoading && !projectData}
            >
              {projectData?.project_rule ? (
                <RuleSummary rule={projectData.project_rule} />
              ) : (
                <Text type="secondary">无</Text>
              )}
            </RuleLevelCard>

            <ConnectorRow text="对不适用于此项目级规则的特定库存单元，可查看或设置库存单元级规则">
              {/* 点这个按钮才显示/收起"库存单元级规则"卡片；收起卡片时一并收起下面的库存单元列表 */}
              <ConfigProvider theme={{ token: { colorPrimary: LEVEL_COLOR.inventory_unit } }}>
                <Button
                  disabled={!projectData}
                  onClick={() => {
                    if (unitCardOpen) setUnitListOpen(false);
                    setUnitCardOpen((open) => !open);
                  }}
                >
                  {unitCardOpen ? '收起库存单元级规则设置' : '展开库存单元级规则设置'}
                </Button>
              </ConfigProvider>
            </ConnectorRow>

            {unitCardOpen && (
              <RuleLevelCard
                level="inventory_unit"
                title={LEVEL_NAME.inventory_unit}
                titleExtra={projectLabel ? `所属项目: ${projectLabel}` : undefined}
                description={LEVEL_DESCRIPTION.inventory_unit}
                // 库存单元列表默认不展开也不加载：点卡片里的"设置"才加载并显示（绿底白字），再点"收起"隐藏
                onEdit={() => setUnitListOpen((open) => !open)}
                editLabel={unitListOpen ? '收起' : '设置'}
                editPrimary={!unitListOpen}
                hasRule={!!summary?.unit}
                loading={dataLoading && !projectData}
                // 列表在卡片内原地展开，整个板块只有这一圈绿色边框
                expandedContent={unitListOpen ? unitListContent : undefined}
              >
                {summary?.unit ? (
                  <div>
                    <span style={{ fontWeight: 600 }}>{summary.unit}</span> 个库存单元已单独设置规则
                    {/* 列表展开时就紧挨在下面，不需要再提示去哪里看 */}
                    {!unitListOpen && (
                      <div style={{ marginTop: 4, fontSize: 12, color: '#999' }}>
                        点击"设置"查看并管理，其余单元沿用上级规则
                      </div>
                    )}
                  </div>
                ) : (
                  <Text type="secondary">无</Text>
                )}
              </RuleLevelCard>
            )}
          </>
        )}
      </Space>

      <RuleHelpModal open={helpOpen} onClose={() => setHelpOpen(false)} />

      {bulkOpen && projectId && (
        <BulkApplyRuleModal
          open
          projectId={projectId}
          targets={bulkTargets}
          onClose={() => setBulkOpen(false)}
          onDone={() => {
            setSelectedIds([]);
            setSelectAllFiltered(false);
            reloadAll();
          }}
        />
      )}

      {drawer && (
        <RuleFormDrawer
          open
          onClose={() => setDrawer(null)}
          onSaved={reloadAll}
          scopeLevel={drawer.scopeLevel}
          scopeId={drawer.scopeId}
          customerId={drawer.customerId}
          scopeLabel={drawer.scopeLabel}
          existingRule={drawer.existingRule}
        />
      )}
    </div>
  );
};

export default ReturnInspectionRulesPage;
